import { formatDateTime } from "@/api/datetime";
import { logAppEvent } from "@/api/logs";
import { useDialog } from "@/components/DialogProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelectPopover } from "@/components/ui/searchable-select-popover";
import { Separator } from "@/components/ui/separator";
import { SpecialtyAutocomplete } from "@/components/ui/specialty-autocomplete";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WorkerSchedulesTab } from "@/components/worker-schedules-tab";
import { WorkerShifts } from "@/components/worker-shifts";
import { WorkerAssignedProjects } from "@/components/workers/WorkerAssignedProjects";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { useUsers } from "@/features/users/hooks/useUsers";
import { workUnitService } from "@/features/work-units/api/workUnitService";
import { getProjectWorkerAssignments } from "@/lib/projectAssignmentHelpers";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { doc, onSnapshot, Timestamp, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { ArrowLeft, Camera, DollarSign, Edit, Save, User, Wrench, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { db, storage } from "../../../../../firebase";
import { isWorkerAssignedToProject, type WorkerModel } from "../../../../../firestore_types";

export const Route = createFileRoute("/dashboard/agency/workers/$workerId/")({
  component: WorkerProfile,
});

function WorkerProfile() {
  const { workerId } = Route.useParams();
  const { confirm } = useDialog();
  const { user } = useAuth();
  const router = useRouter();
  const { projects } = useProjects();
  const { users } = useUsers();
  const [worker, setWorker] = useState<WorkerModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingProfilePicture, setUploadingProfilePicture] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    managerId: "",
    payRate: "",
    specialties: [] as string[],
    gender: "" as "" | "Male" | "Female",
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, "Workers", workerId),
      (workerDoc) => {
        if (workerDoc.exists()) {
          const workerData = {
            ...workerDoc.data(),
            id: workerDoc.id,
          } as WorkerModel;

          // Verify this worker belongs to the current agency
          if (workerData.agencyId !== user?.uid) {
            toast.error("You don't have permission to view this worker");
            router.history.back();
            return;
          }

          setWorker(workerData);

          // Initialize edit form data
          setEditFormData({
            name: workerData.name || "",
            phoneNumber: workerData.phoneNumber || "",
            email: workerData.email || "",
            managerId: workerData.managerId || "",
            payRate: workerData.payRate?.toString() || "",
            specialties: workerData.specialties || [],
            gender: workerData.gender || "",
          });
        } else {
          setWorker(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching worker:", error);
        toast.error("Failed to load worker profile");
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [workerId, user?.uid, router]);

  // Filter projects to only those assigned to this agency
  const agencyProjects = useMemo(() => {
    if (!projects || !user?.uid) return [];
    return projects.filter((project) => project.assignedAgencyId === user.uid);
  }, [projects, user?.uid]);

  const getAssignedProjects = () => {
    return agencyProjects.filter((project) => isWorkerAssignedToProject(project, workerId));
  };

  const getInactiveAssignedProjects = () => {
    return agencyProjects.filter((project) => {
      const assignments = getProjectWorkerAssignments(project);
      return assignments.some((a) => a.workerId === workerId && a.inactive);
    });
  };

  const getManagerName = (managerId?: string) => {
    if (!managerId) {
      return "No Manager";
    }
    const manager = (users || []).find((user) => user.uid === managerId && user.type === "manager");
    return manager?.name || "Unknown Manager";
  };

  const handleToggleAssignment = async (projectId: string, isCurrentlyInactive: boolean) => {
    try {
      const project = agencyProjects.find((p) => p.id === projectId);
      if (!project) return;

      if (project.workUnits) {
        // Find which work unit contains this worker
        const workUnit = project.workUnits.find((wu) =>
          wu.attachedWorkers?.some((w) => w.workerId === workerId),
        );
        if (!workUnit) {
          toast.error("Worker not found in any work unit");
          return;
        }

        await workUnitService.toggleWorkerStatus(
          projectId,
          workUnit.id,
          workerId,
          isCurrentlyInactive,
        );
      }

      toast.success(`Assignment ${isCurrentlyInactive ? "activated" : "deactivated"} successfully`);
    } catch (error) {
      console.error("Error toggling assignment:", error);
      toast.error("Failed to update assignment status");
    }
  };

  const handleEdit = () => {
    if (!worker) return;

    // Reset form data to current worker data
    setEditFormData({
      name: worker.name || "",
      phoneNumber: worker.phoneNumber || "",
      email: worker.email || "",
      managerId: worker.managerId || "",
      payRate: worker.payRate?.toString() || "",
      specialties: worker.specialties || [],
      gender: worker.gender || "",
    });
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    if (!worker) return;

    // Reset form data
    setEditFormData({
      name: worker.name || "",
      phoneNumber: worker.phoneNumber || "",
      email: worker.email || "",
      managerId: worker.managerId || "",
      payRate: worker.payRate?.toString() || "",
      specialties: worker.specialties || [],
      gender: worker.gender || "",
    });
  };

  const handleSaveEdit = async () => {
    if (!worker) return;

    setIsSaving(true);
    try {
      const workerRef = doc(db, "Workers", workerId);
      await updateDoc(workerRef, {
        name: editFormData.name,
        phoneNumber: editFormData.phoneNumber,
        email: editFormData.email || null,
        managerId: editFormData.managerId,
        payRate: editFormData.payRate ? parseFloat(editFormData.payRate) : null,
        specialties: editFormData.specialties.length > 0 ? editFormData.specialties : null,
        gender: editFormData.gender || null,
        updatedAt: Timestamp.now(),
      });

      logAppEvent("worker_updated", {
        workerId,
        name: editFormData.name,
        phoneNumber: editFormData.phoneNumber,
      });

      toast.success("Worker updated successfully");
      setIsEditMode(false);
    } catch (error) {
      console.error("Error updating worker:", error);
      toast.error("Failed to update worker. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const getManagerOptions = () => {
    return (users || []).filter((user) => user.type === "manager");
  };

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (file.size > maxSize) {
      toast.info("Profile picture size must be less than 5MB");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.info("Only images (JPEG, PNG, GIF) are allowed for profile pictures");
      return;
    }

    setUploadingProfilePicture(true);

    try {
      const fileName = `profile-${Date.now()}.${file.name.split(".").pop()}`;
      const storageRef = ref(storage, `workers/${workerId}/profile/${fileName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Delete old profile picture if it exists
      if (worker?.profilePicture) {
        try {
          const oldRef = ref(storage, worker.profilePicture);
          await deleteObject(oldRef);
        } catch (error) {
          console.warn("Could not delete old profile picture:", error);
        }
      }

      // Update worker document with new profile picture
      await updateDoc(doc(db, "Workers", workerId), {
        profilePicture: downloadURL,
      });

      // Update local state
      setWorker((prev) => (prev ? { ...prev, profilePicture: downloadURL } : null));

      toast.info("Profile picture updated successfully");
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      toast.info("Failed to upload profile picture");
    } finally {
      setUploadingProfilePicture(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!worker?.profilePicture) return;
    if (!(await confirm({ description: "Are you sure you want to remove the profile picture?" })))
      return;

    try {
      // Delete from Firebase Storage
      const storageRef = ref(storage, worker.profilePicture);
      await deleteObject(storageRef);

      // Update worker document
      await updateDoc(doc(db, "Workers", workerId), {
        profilePicture: null,
      });

      // Update local state
      setWorker((prev) => (prev ? { ...prev, profilePicture: null } : null));

      toast.info("Profile picture removed successfully");
    } catch (error) {
      console.error("Error removing profile picture:", error);
      toast.info("Failed to remove profile picture");
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">Loading worker profile...</div>
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold">Worker not found</h2>
          <p className="text-gray-600">The worker you are looking for does not exist.</p>
        </div>
      </div>
    );
  }

  const assignedProjects = getAssignedProjects();

  return (
    <div className="container mx-auto max-w-4xl p-1 sm:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => router.history.back()}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Worker Profile</h1>
        </div>
        <div className="flex items-center gap-2">
          {isEditMode ? (
            <>
              <Button
                variant="default"
                size="sm"
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="bg-green-600 hover:bg-green-700"
              >
                <Save className="mr-2 h-4 w-4" />
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={handleEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
          <TabsTrigger value="time_entries">Time Entries</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <div className="grid grid-cols-1 gap-6">
            {/* Main Profile Info */}
            <div className="space-y-6">
              {/* Profile Picture Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Profile Picture</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfilePictureUpload}
                        className="hidden"
                        id="profile-picture-upload"
                      />
                      <Button
                        size="sm"
                        onClick={() => document.getElementById("profile-picture-upload")?.click()}
                        disabled={uploadingProfilePicture}
                      >
                        <Camera className="mr-2 h-4 w-4" />
                        {worker?.profilePicture ? "Change" : "Upload"}
                      </Button>
                      {worker?.profilePicture && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleRemoveProfilePicture}
                          disabled={uploadingProfilePicture}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-center">
                    {uploadingProfilePicture ? (
                      <div className="flex h-32 w-32 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                      </div>
                    ) : worker?.profilePicture ? (
                      <img
                        src={worker.profilePicture}
                        alt={`${worker.name} profile`}
                        className="h-32 w-32 rounded-lg border-2 border-gray-200 object-cover"
                      />
                    ) : (
                      <div className="flex h-32 w-32 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50">
                        <div className="text-center">
                          <User className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                          <p className="text-sm text-gray-500">No profile picture</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Worker Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{worker.name}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant={worker.isTestNumber ? "secondary" : "default"}>
                        {worker.isTestNumber ? "Test" : "Live"}
                      </Badge>
                      {worker.type === "app_worker" && <Badge variant="outline">App Worker</Badge>}
                    </div>
                  </CardTitle>
                  <CardDescription>Worker ID: {worker.id}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isEditMode ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={editFormData.name}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, name: e.target.value })
                          }
                          placeholder="Enter worker name"
                        />
                      </div>
                      <div>
                        <Label>Phone Number *</Label>
                        <Input
                          value={editFormData.phoneNumber}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, phoneNumber: e.target.value })
                          }
                          placeholder="Enter phone number"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, email: e.target.value })
                          }
                          placeholder="Enter email address"
                        />
                      </div>
                      <div>
                        <Label>Manager *</Label>
                        <SearchableSelectPopover
                          items={getManagerOptions()}
                          selectedValue={editFormData.managerId}
                          onSelect={(value) =>
                            setEditFormData({ ...editFormData, managerId: value || "" })
                          }
                          getKey={(manager) => manager.uid}
                          getLabel={(manager) => manager.name}
                          triggerLabel={
                            editFormData.managerId
                              ? getManagerName(editFormData.managerId)
                              : "Select Manager"
                          }
                          placeholder="Search managers..."
                          emptyText="No managers found"
                          buttonClassName="w-full justify-between"
                          contentClassName="w-full p-0"
                        />
                      </div>
                      <div>
                        <Label>Pay Rate ($/hr)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editFormData.payRate}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, payRate: e.target.value })
                          }
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label>Gender</Label>
                        <select
                          value={editFormData.gender}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              gender: e.target.value as "" | "Male" | "Female",
                            })
                          }
                          className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium text-gray-600">Phone Number</label>
                        <p className="mt-1">{formatPhoneNumber(worker.phoneNumber)}</p>
                      </div>
                      {worker.email && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Email</label>
                          <p className="mt-1">{worker.email}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-600">Manager</label>
                        <p className="mt-1">{getManagerName(worker.managerId)}</p>
                      </div>
                      {worker.payRate && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Pay Rate</label>
                          <div className="mt-1 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-green-600" />
                            <span>${worker.payRate.toFixed(2)}/hr</span>
                          </div>
                        </div>
                      )}
                      {worker.gender && (
                        <div>
                          <label className="text-sm font-medium text-gray-600">Gender</label>
                          <p className="mt-1">{worker.gender}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-600">Created At</label>
                        <p className="mt-1">{formatDateTime(worker.createdAt)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Last Updated</label>
                        <p className="mt-1">{formatDateTime(worker.updatedAt)}</p>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {isEditMode ? (
                    <div>
                      <Label>Specialties</Label>
                      <div className="space-y-2">
                        <SpecialtyAutocomplete
                          value=""
                          onChange={(specialty) => {
                            if (specialty && !editFormData.specialties.includes(specialty)) {
                              setEditFormData({
                                ...editFormData,
                                specialties: [...editFormData.specialties, specialty],
                              });
                            }
                          }}
                          placeholder="Add Experience"
                        />
                        <div className="flex flex-wrap gap-2">
                          {editFormData.specialties.map((specialty) => (
                            <Badge key={specialty} variant="outline" className="text-sm">
                              {specialty}
                              <button
                                type="button"
                                onClick={() =>
                                  setEditFormData({
                                    ...editFormData,
                                    specialties: editFormData.specialties.filter(
                                      (s) => s !== specialty,
                                    ),
                                  })
                                }
                                className="ml-1 text-red-500 hover:text-red-700"
                              >
                                ×
                              </button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    worker.specialties &&
                    worker.specialties.length > 0 && (
                      <div>
                        <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-600">
                          <Wrench className="h-4 w-4" />
                          Specialties
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {worker.specialties.map((specialty) => (
                            <Badge key={specialty} variant="outline" className="text-xs">
                              {specialty}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )
                  )}

                  <Separator />

                  <div>
                    <label className="text-sm font-medium text-gray-600">Clock Status</label>
                    <div className="mt-2 space-y-1">
                      {worker.lastClockedInAt && (
                        <p className="text-sm">
                          Last clocked in: {formatDateTime(worker.lastClockedInAt)}
                        </p>
                      )}
                      {worker.lastClockedOutAt && (
                        <p className="text-sm">
                          Last clocked out: {formatDateTime(worker.lastClockedOutAt)}
                        </p>
                      )}
                      {!worker.lastClockedInAt && !worker.lastClockedOutAt && (
                        <p className="text-sm text-gray-500">No clock activity yet</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Assigned Projects */}
              <WorkerAssignedProjects
                workerId={workerId}
                workerName={worker.name}
                assignedProjects={assignedProjects}
                inactiveProjects={getInactiveAssignedProjects()}
                allProjects={agencyProjects}
                onToggleAssignment={handleToggleAssignment}
                isAdmin={false}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="schedules" className="mt-6">
          <WorkerSchedulesTab workerId={workerId} workerName={worker.name} />
        </TabsContent>

        <TabsContent value="time_entries" className="mt-6">
          <WorkerShifts workerId={workerId} workerName={worker.name} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
